import Service from "@/code/service";
import { setService, initialLoadDone } from "@/views/service/main";
import { ServiceSpec } from "@/views/service/service-spec";
import { reactive, Ref, ref } from "vue";
import { skapi, ENDPOINTS } from "@/main";
import { SkapiError } from "skapi-js";

export let serviceIdList: Ref<string[]> = ref([]);
export let serviceList: { [key: string]: Service } = reactive({});
export let serviceSpecList: { [key: string]: ServiceSpec } = reactive({});
export let fetchingServiceList = ref(true);
export let allServicesLoaded = ref(false);
export let serviceRaw: any = {};
export let notExist = ref(false);
export let pendServiceFetchError = ref(false); // pend- 로드 실패 감지용
// let pendingLoads = 0; // 현재 로딩 중인 Service.load 호출 수

export let endOfList = ref(false);
export let pullServicesRunning = ref(false);
let firstLoadService = "";
export let pullServices = async (options?: { firstLoadServiceId?: string }) => {
    let {
        admin_private_endpoint,
        admin_public_endpoint,
        record_private_endpoint,
    } = await ENDPOINTS;
    let firstLoadId = options?.firstLoadServiceId || null;
    let requestOptions = {
        service: skapi.service,
        owner: skapi.owner,
        // bypass_cached_service: true,
    };

    notExist.value = false;
    pullServicesRunning.value = true;

    // fetchOptions 설정 - firstLoadId가 있거나 첫 로드일 때는 fetchMore 사용 안 함
    let fetchOptions: any = {
        limit: 25,
    };

    // firstLoadId가 없고, 이미 서비스를 불러온 적이 있을 때만 fetchMore 사용
    if (!firstLoadId && serviceIdList.value.length > 0 && !endOfList.value) {
        fetchOptions.fetchMore = true;
    }

    if (firstLoadId) {
        // 만약에 경로에 있는 서비스 아이디 앞에 pend- 가 붙어있다면 제거
        if (firstLoadId.startsWith("pend-")) {
            firstLoadId = firstLoadId.replace("pend-", "");
        }
        Object.assign(requestOptions, {
            service_id: firstLoadId,
        });
        firstLoadService = firstLoadId;
    }
    if (firstLoadService) {
        // 이미 불러온 서비스가 있으면 제외
        Object.assign(requestOptions, {
            service_id_to_exclude: serviceIdList.value,
        });
    }

    await skapi.util
        .request(admin_private_endpoint + "pull-services", requestOptions, {
            auth: true,
            fetchOptions,
        })
        .then(async (srvcs) => {
            let options = {};

            if (firstLoadId) {
                if (srvcs?.list.length) {
                    serviceIdList.value.push(srvcs?.list[0].service);
                    serviceRaw[srvcs?.list[0].service] = srvcs?.list[0];
                    // 특정 서비스로 바로 진입하는 경우
                    options = { service: srvcs?.list[0] };
                } else {
                    // 특정 서비스가 없는 경우
                    firstLoadId = null;
                    notExist.value = true;
                }
            } else if (srvcs?.list.length) {
                endOfList.value = srvcs?.endOfList;

                // 일반적인 서비스 목록 받아오기
                for (let service of srvcs?.list) {
                    serviceIdList.value.push(service.service);
                    serviceRaw[service.service] = service;
                }
                options = { rest: srvcs?.list };
            } else {
                // 서비스가 하나도 없는 경우
                endOfList.value = true;
            }

            // 다 불러오고 난 후에 timestamp 순으로 정렬 체크
            serviceIdList.value.sort((a, b) => {
                let timeA =
                    new Date(
                        serviceList[a]?.service?.timestamp ||
                            serviceRaw[a]?.timestamp ||
                            0,
                    ).getTime() || 0;
                let timeB =
                    new Date(
                        serviceList[b]?.service?.timestamp ||
                            serviceRaw[b]?.timestamp ||
                            0,
                    ).getTime() || 0;
                return timeB - timeA;
            });

            fetchingServiceList.value = false;
            pullServicesRunning.value = false;

            // 라우터 이동이 완료될 때까지 대기
            const router = (await import("@/router")).default;
            await router.isReady();
            await loadServiceList(options);
        })
        .catch((error) => {
            fetchingServiceList.value = false;
            pullServicesRunning.value = false;
            if (!(error instanceof SkapiError)) throw error;
        });
};
export let loadServiceList = async (options?: {
    service?: object;
    rest?: any;
}) => {
    let serviceId = (options?.service as any)?.service || "";
    // 특정 서비스로 바로 진입인지 확인
    if (options?.service && !serviceList[serviceId]) {
        try {
            const serviceObj = await Service.load(options.service);

            if (serviceObj && serviceObj?.service?.service) {
                serviceList[serviceId] = serviceObj;
                serviceSpecList[serviceId] = new ServiceSpec(serviceObj);
                setService(serviceSpecList[serviceId]);
            }
        } catch (e) {
            if (e.message === "Service load failed.") {
                pendServiceFetchError.value = true;
                return;
            }
        }
    } else if (serviceId) {
        setService(serviceSpecList[serviceId]);
    } else if (options?.rest && options?.rest.length > 0) {
        let restArray = options.rest;
        // 나머지 서비스 로드
        await Promise.all(
            restArray.map((service) =>
                Service.load(service).then((serviceObj) => {
                    if (serviceObj) {
                        serviceList[service.service] = serviceObj;
                        serviceSpecList[service.service] = new ServiceSpec(
                            serviceObj,
                        );
                        if (!initialLoadDone.value) {
                            initialLoadDone.value = true;
                        }
                    }
                }),
            ),
        );
    }

    allServicesLoaded.value = true;
};

// export let loadServiceList = async () => {
//     const router = (await import("@/router")).default;
//     let firstLoadServiceId = "";
//     notExist.value = false;

//     try {
//         const resolved = router.resolve(
//             window.location.pathname + window.location.search,
//         );
//         firstLoadServiceId = (resolved.params?.service as string) || "";
//     } catch (e) {
//         const currentRoute = router.currentRoute?.value;
//         firstLoadServiceId = (currentRoute?.params?.service as string) || "";
//     }

//     // serviceIdList 안에 없는 ID면 무시
//     if (
//         firstLoadServiceId.length > 0 &&
//         !firstLoadServiceId.includes("pend-") &&
//         !serviceIdList.includes(firstLoadServiceId)
//     ) {
//         firstLoadServiceId = "";
//         notExist.value = true;
//     }

//     const rest = serviceIdList.filter((id) => id !== firstLoadServiceId);
//     allServicesLoaded.value = false;

//     // 1. 먼저 특정 서비스 로드
//     if (firstLoadServiceId && !serviceList[firstLoadServiceId]) {
//         try {
//             const serviceObj = await Service.load(
//                 serviceRaw[firstLoadServiceId] || firstLoadServiceId,
//             );
//             if (serviceObj && serviceObj?.service?.service) {
//                 serviceList[firstLoadServiceId] = serviceObj;
//                 serviceSpecList[firstLoadServiceId] = new ServiceSpec(
//                     serviceObj,
//                 );
//                 setService(serviceSpecList[firstLoadServiceId]);
//             }
//         } catch (e) {
//             if (e.message === "Service load failed.") {
//                 pendServiceFetchError.value = true;
//                 return;
//             }
//         }
//     } else if (firstLoadServiceId) {
//         setService(serviceSpecList[firstLoadServiceId]);
//     }

//     // 2. 나머지는 병렬로 로드 (중복 제거 포함)
//     const restToLoad = rest.filter((id) => !serviceList[id]);

//     await Promise.all(
//         restToLoad.map((serviceId) =>
//             Service.load(serviceRaw[serviceId] || serviceId).then(
//                 (serviceObj) => {
//                     if (serviceObj) {
//                         serviceList[serviceId] = serviceObj;
//                         serviceSpecList[serviceId] = new ServiceSpec(
//                             serviceObj,
//                         );

//                         if (!initialLoadDone.value) {
//                             initialLoadDone.value = true;
//                         }
//                     }
//                 },
//             ),
//         ),
//     );

//     allServicesLoaded.value = true;
// };
